/**
 * Upload with retry and main handoff. Per file: persist to SQLite first, then S3 with 3 fast retries;
 * on success mark uploaded; on failure enqueue for main.
 */

import S3Utils from '@renderer/utils/s3Utils'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import {
  hasPreloadAPI,
  saveFileToDB,
  markMediaUploaded,
  enqueueUpload
} from '@renderer/utils/pipeline/bridgeApi'
import type {
  ProcessedFile,
  ProcessingContext,
  UploadFailure,
  UploadResult,
  ResourceType
} from '@renderer/types/outputPipeline.types'
import { parseGalleryRefusal, type GalleryRefusal } from '@renderer/utils/pipeline/uploadOutcome'
import { resolveGallerySessionUrlFromStore } from '@renderer/utils/galleryUrlUtils'
import { isDigitalMode } from '@renderer/lib/runtime'
import { useSessionStateStore } from '@renderer/stores/sessionState'

const MAX_RETRIES = 3
const BAHELA_DEFAULT_PROJECT_ID = '691d305ea5b85a88793907bb'
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

/**
 * The project id to stamp on uploads. Prefers the session store's projectId
 * (authoritative — always the resolved Mongo id, see initializeSession), then
 * the per-item context, then the legacy bahela default. Guarantees a slug
 * (digital route param) never reaches S3 keys or POST /api/gallery (→ CastError
 * 500). Kiosk is unaffected — all three sources are the same ObjectId.
 */
function resolveUploadProjectId(ctxProjectId?: string): string {
  const sessionPid = useSessionStateStore.getState().projectId
  if (sessionPid && OBJECT_ID_RE.test(sessionPid)) return sessionPid
  const ctxPid = ctxProjectId?.trim()
  if (ctxPid) return ctxPid
  return BAHELA_DEFAULT_PROJECT_ID
}
const RETRY_DELAYS_MS = [1000, 2000, 4000]

function deriveResourceId(sessionId: string, fileName: string): string {
  const base = fileName.replace(/\.[^/.]+$/, '')
  return `${sessionId}_${base}`
}

function uploadTypeToResourceType(uploadType: ProcessedFile['uploadType']): ResourceType {
  if (uploadType === 'strip' || uploadType === 'image') return 'photo'
  if (uploadType === 'gif') return 'gif'
  return 'video'
}

function calculateExpirationDate(subscriptionType: string | null): Date {
  const now = new Date()
  switch (subscriptionType) {
    case 'enterprise':
      return new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
    case 'professional':
      return new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)
    case 'starter':
      return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    default:
      return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  }
}

interface S3Response {
  url: string
  key: string
  thumbnailUrl?: string
  metadata: {
    originalFilename: string
    contentType: string
    size: string
    width?: string
    height?: string
  }
}

async function uploadToS3(
  file: File,
  ctx: ProcessingContext
): Promise<S3Response> {
  const userId = ctx.context?.user?.email || ''
  const projectId = resolveUploadProjectId(ctx.projectId)
  const response = await S3Utils.uploadFile(
    file,
    userId,
    projectId,
    ctx.sessionId,
    ctx.galleryId,
    ctx.eventId
  )
  return {
    url: response.url,
    key: response.key,
    thumbnailUrl: response.thumbnailUrl,
    metadata: response.metadata
  }
}

/** A non-2xx answer from POST /api/gallery, read into the upload gate's code and terminal flag. */
class GalleryRegistrationError extends Error {
  readonly refusal: GalleryRefusal

  constructor(refusal: GalleryRefusal) {
    super(`Gallery update failed: ${refusal.status} ${refusal.message}`)
    this.refusal = refusal
  }
}

async function updateGalleryAPI(
  ctx: ProcessingContext,
  uploadType: ProcessedFile['uploadType'],
  response: S3Response
): Promise<void> {
  const subscriptionType = useSubscriptionStore.getState().type
  const expiresAt = calculateExpirationDate(subscriptionType)
  const mediaItem = {
    key: response.key,
    url: response.url,
    thumbnailUrl: response.thumbnailUrl,
    type: uploadType,
    createdAt: new Date(),
    expiresAt,
    metadata: {
      originalFilename: response.metadata.originalFilename,
      contentType: response.metadata.contentType,
      size: Number(response.metadata.size),
      ...(response.metadata.width && response.metadata.height && {
        width: Number(response.metadata.width),
        height: Number(response.metadata.height)
      })
    }
  }

  const normalizedProjectId = resolveUploadProjectId(ctx.projectId)
  const galleryUrl = resolveGallerySessionUrlFromStore(ctx.sessionId)
  const createGalleryUrl = `${process.env.VITE_API_URL}/api/gallery`

  const res = await fetch(createGalleryUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      sessionId: ctx.sessionId,
      userId: ctx.context?.user?.email || '',
      projectId: normalizedProjectId,
      eventId: ctx.eventId,
      mediaItem,
      url: galleryUrl,
      token: useSettingsStore.getState().activationCode || undefined,
      deviceId: useSettingsStore.getState().deviceId || undefined,
      // Digital sessions get a fixed short retention server-side, independent
      // of the owner's subscription tier (see /api/gallery).
      ...(isDigitalMode() ? { source: 'digital' } : {})
    })
  })

  if (!res.ok) {
    throw new GalleryRegistrationError(parseGalleryRefusal(res.status, await res.text()))
  }
}

/**
 * Single-file upload: persist to SQLite first (when preload exists), then S3 with 3 retries,
 * then register in the gallery with its own 3 retries. The two phases retry independently so
 * a gallery-only failure never re-PUTs the (already-uploaded) S3 object. The row is marked
 * uploaded ONLY after gallery registration succeeds — gallery presence is what the customer
 * sees and what reconcile checks — so a gallery failure hands off to main instead of leaving
 * a green-but-invisible false positive.
 */
export async function uploadFile(
  processed: ProcessedFile,
  ctx: ProcessingContext
): Promise<UploadResult> {
  const localUrl = URL.createObjectURL(processed.file)

  if (ctx.downloadLocal) {
    const a = document.createElement('a')
    a.href = localUrl
    a.download = processed.file.name
    a.setAttribute('data-automated-click', 'true')
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const hasPreload = hasPreloadAPI()
  let resourceId: string | undefined

  if (hasPreload) {
    const arrayBuffer = await processed.file.arrayBuffer()
    const saveResult = await saveFileToDB({
      sessionId: ctx.sessionId,
      fileName: processed.fileName,
      fileType: processed.fileType,
      data: arrayBuffer,
      metadata: { ...processed.metadata, alreadyUploaded: false }
    })
    if (saveResult.success) {
      // A skipped save still means the row is there (reason: 'duplicate'), so the
      // resourceId is valid and the failure handoffs below can still reach main.
      resourceId = saveResult.resourceId ?? deriveResourceId(ctx.sessionId, processed.fileName)
    } else {
      // Nothing persisted, so there is no row for a background job to retry from:
      // if S3 now fails, this file is gone for good. Never let that pass quietly.
      console.error(
        `[uploadQueue] SQLite save failed for ${processed.fileName} (${saveResult.error ?? 'unknown'}) — ` +
          'no retry row, this upload cannot be recovered by the background queue'
      )
    }
  }

  if (!ctx.context?.user?.email) {
    // The renderer cannot upload without an owner email, but main can: its queue
    // resolves auth itself (session cookie / persisted token). Hand off exactly like
    // the S3 and gallery failure paths below instead of returning silently — otherwise
    // the file waits for the next reconcile sweep (up to ~35 min) and is lost entirely
    // if the booth is closed before then.
    console.error(
      `[uploadQueue] No user email in context — handing ${processed.fileName} to main`
    )
    if (hasPreload && resourceId) {
      await enqueueUpload({ sessionId: ctx.sessionId, resourceId })
    }
    return { localUrl }
  }

  // Phase 1 — upload the bytes to S3, retrying ONLY the PUT. Once this succeeds we
  // hold onto the response and never re-upload, so a later gallery failure can't
  // orphan duplicate S3 objects with fresh keys.
  let s3Response: S3Response | null = null
  let s3Error = ''
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      s3Response = await uploadToS3(processed.file, ctx)
      break
    } catch (err) {
      const lastError = err instanceof Error ? err : new Error(String(err))
      console.error('[uploadQueue] Error uploading file to S3:', lastError.message)
      s3Error = lastError.message
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]))
      }
    }
  }

  if (!s3Response) {
    // The bytes never reached S3 — hand off to main so the background job retries.
    if (hasPreload && resourceId) {
      await enqueueUpload({ sessionId: ctx.sessionId, resourceId })
    }
    return { localUrl, failure: { kind: 'retryable', stage: 's3', message: s3Error } }
  }

  // Phase 2 — register the already-uploaded object in the gallery, retrying ONLY the
  // POST. Gallery registration is what makes the media queryable by the customer and
  // is the proof reconcile checks, so the row is marked uploaded only after it
  // succeeds. A file on S3 that is NOT registered must never read as uploaded.
  let galleryFailure: UploadFailure = { kind: 'retryable', stage: 'gallery', message: '' }
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      await updateGalleryAPI(ctx, processed.uploadType, s3Response)
      // Registered — the media is live. Marking the local flag is best-effort: if it
      // fails, reconcile will set uploaded=1 from server proof. Never re-run the
      // upload just because this local DB write failed.
      if (hasPreload && resourceId) {
        try {
          await markMediaUploaded({
            sessionId: ctx.sessionId,
            resourceId,
            resourceType: uploadTypeToResourceType(processed.uploadType),
            fileName: processed.fileName
          })
        } catch (markErr) {
          console.warn(
            '[uploadQueue] markMediaUploaded failed after gallery success (reconcile will fix):',
            markErr
          )
        }
      }
      return { localUrl, uploadedUrl: s3Response.url, s3Key: s3Response.key }
    } catch (err) {
      const lastError = err instanceof Error ? err : new Error(String(err))
      console.error('[uploadQueue] Error registering media in gallery:', lastError.message)
      const refusal = err instanceof GalleryRegistrationError ? err.refusal : null
      if (refusal?.terminal && refusal.code) {
        // The gate marked it terminal (no licence for this device and no credits, or no
        // such owner): three more tries 1-4 s apart cannot change that.
        galleryFailure = {
          kind: 'refused',
          status: refusal.status,
          code: refusal.code,
          message: refusal.message
        }
        break
      }
      galleryFailure = { kind: 'retryable', stage: 'gallery', message: lastError.message }
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]))
      }
    }
  }

  // On S3 but not registered after retries — do NOT mark uploaded. The row stays
  // uploaded=0/pending, so the reconcile sweep (startup + 30-min timer) or an operator
  // retry will re-queue a background job that re-registers it in the gallery. The
  // handoff below just re-syncs the session record for main. A refusal is handed off
  // too: main authenticates with its own stored licence and may be let in.
  if (hasPreload && resourceId) {
    await enqueueUpload({ sessionId: ctx.sessionId, resourceId })
  }
  return { localUrl, failure: galleryFailure }
}
